import * as Yup from 'yup';
import { useCallback, useMemo, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFieldArray } from 'react-hook-form';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
// hooks 
import { useResponsive } from 'src/hooks/use-responsive';
// types
import { LoSam, LoSamApi, sensorModels } from 'src/types/losam';
// components
import { CustomFile } from 'src/components/upload';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFEditor, RHFUpload, RHFTextField, RHFAutocomplete, RHFSwitch } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { paths } from 'src/routes/paths';
import { LoSamCamera } from 'src/types/camera';
import { Chip } from '@mui/material';
import { STATUS_OPTIONS } from 'src/constants/status';
import CustomLoadingButton from 'src/components/button/button-loadding-dark-model';
import { useLoSam } from './hooks/use-losam';
import useVuonTrong from '../vuontrong/hooks/use-vuontrong';

// ----------------------------------------------------------------------

type Props = {
  currentLoSam?: LoSam;
  onClose: VoidFunction;
  onSave?: (data: Partial<LoSamApi>, file?: File) => Promise<void>;
};

interface FormValuesProps {
  vuonTrong_ID: number;
  tenLo: string;
  maLo: string;
  dienTich: number;
  loai: string;
  ghiChu: string;
  soHang: number;
  soCot: number;
  is_public: boolean;
  trangThai: number;
  coverUrl?: CustomFile | string | null;
  chiTietLoSam: {
    loSamLoaiTuoiId: number;
    tuoi: string;
    soLuong: number;
    trangThai: number;
  }[];
  cameras: LoSamCamera[];
  sensorIds: number[];
}

export default function LoSamNewEditForm({ currentLoSam, onClose, onSave }: Props) {
  const isEdit = !!currentLoSam;

  const mdUp = useResponsive('up', 'md');

  const { enqueueSnackbar } = useSnackbar();

  const {
    loaiTuoiOptions,
    loaiCameraOptions,
    addLoSam,
    editLoSam,
    uploadImage,
  } = useLoSam();
  const { gardens } = useVuonTrong();

  const NewLoSamSchema = Yup.object().shape({
    tenLo: Yup.string().required('Tên lô là bắt buộc'),
    maLo: Yup.string().required('Mã lô là bắt buộc'),
    dienTich: Yup.number()
      .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
      .required('Diện tích là bắt buộc')
      .min(0, 'Diện tích phải lớn hơn 0'),
    loai: Yup.string().required('Loại là bắt buộc'),
    ghiChu: Yup.string(),
    soHang: Yup.number()
      .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
      .required('Số hàng là bắt buộc')
      .min(1, 'Số hàng phải lớn hơn 0'),
    soCot: Yup.number()
      .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
      .required('Số cột là bắt buộc')
      .min(1, 'Số cột phải lớn hơn 0'),
    is_public: Yup.boolean(),
    coverUrl: Yup.mixed().nullable(),
    chiTietLoSam: Yup.array().of(
      Yup.object().shape({
        loSamLoaiTuoiId: Yup.number()
          .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
          .required('Loại tuổi là bắt buộc'),
        tuoi: Yup.string().required('Tuổi là bắt buộc'),
        soLuong: Yup.number()
          .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
          .required('Số lượng là bắt buộc')
          .min(1, 'Số lượng phải lớn hơn 0'),
        trangThai: Yup.number()
          .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
          .required('Trạng thái là bắt buộc'),
      })
    ),
    cameras: Yup.array().of(
      Yup.object().shape({
        loSamLoaiCameraId: Yup.number()
          .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
          .required('Loại camera là bắt buộc'),
        rtsp: Yup.string().required('RTSP là bắt buộc'),
        userName: Yup.string(), // Thêm trường userName
        password: Yup.string(), // Thêm trường password
      })
    ),
    sensorIds: Yup.array().of(Yup.number()),
  });
const defaultValues = useMemo(
  () => ({
    vuonTrong_ID: currentLoSam?.vuonTrong_ID || 0,
    tenLo: currentLoSam?.tenLo || '',
    maLo: currentLoSam?.maLo || '',
    dienTich: currentLoSam?.dienTich || 0,
    loai: currentLoSam?.loai || '',
    trangThai: currentLoSam?.trangThai || 1,
    ghiChu: currentLoSam?.ghiChu || '',
    soHang: currentLoSam?.soHang || 0,
    soCot: currentLoSam?.soCot || 0,
    is_public: (currentLoSam?.trangThai ?? 1) === 1,
    coverUrl: currentLoSam?.hinhAnh || null,
    chiTietLoSam: [],
    cameras: [],
    sensorIds: [], // ← Thêm dòng này
  }),
  [currentLoSam]
);

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(NewLoSamSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
  } = methods;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'chiTietLoSam',
  });

  const { fields: cameraFields, append: appendCamera, remove: removeCamera, replace: replaceCamera } = useFieldArray({
    control,
    name: 'cameras',
  });

  const values = watch();

  useEffect(() => {
    if (currentLoSam) {
      // Reset primitive fields
      reset(defaultValues);

      // Set image if exists
      if (currentLoSam.hinhAnh) {
        setValue('coverUrl', currentLoSam.hinhAnh);
      }

      // Populate chiTietLoSam from currentLoSam.loSamChiTiets if present
      const mappedChiTiet = (currentLoSam.loSamChiTiets || []).map((ct: any) => ({
        loSamLoaiTuoiId: ct.loSamLoaiTuoiId ?? ct.loSamLoaiTuoi?.id ?? 0,
        tuoi: ct.loSamLoaiTuoi?.tuoi || '',
        soLuong: ct.soLuong ?? 0,
        trangThai: ct.trangThai ?? 1,
      }));
      if (mappedChiTiet.length > 0) {
        replace(mappedChiTiet);
      }

      // Populate cameras from currentLoSam.loSamCameras if present
      const mappedCameras = (currentLoSam.loSamCameras || []).map((cm: any) => ({
        id: cm.id || 0,
        loSamId: cm.loSamId || currentLoSam.loSam_ID || 0,
        loSamLoaiCameraId: cm.loSamLoaiCameraId ?? cm.loSamLoaiCamera?.id ?? 0,
        rtsp: cm.rtsp || '',
        trangThai: cm.trangThai ?? 1,
        userName: cm.userName || '',
        password: cm.password || '',
        action: cm.action ||'',
        onvifCamera: cm.onvifCamera || '',
        ipCamera: cm.ipCamera
      }));
      if (mappedCameras.length > 0) {
        replaceCamera(mappedCameras);
      }
       if (currentLoSam.sensorIds && currentLoSam.sensorIds.length > 0) {
      setValue('sensorIds', currentLoSam.sensorIds);
    }
    }
   
  }, [currentLoSam, defaultValues, reset, replace, replaceCamera, setValue]);

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('coverUrl', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const handleRemoveFile = useCallback(() => {
    setValue('coverUrl', null);
  }, [setValue]);

  const handleAddChiTiet = useCallback(() => {
    append({
      loSamLoaiTuoiId: 1,
      tuoi: '1 năm',
      soLuong: 1,
      trangThai: 1,
    });
  }, [append]);

  const handleRemoveChiTiet = useCallback((index: number) => {
    remove(index);
  }, [remove]);

  const handleAddCamera = useCallback(() => {
    appendCamera({
      id: 0,
      loSamId: 0,
      loSamLoaiCameraId: 1,
      rtsp: '',
      trangThai: 1,
      password: '',
      userName: '',
      ipCamera: '',
      onvifCamera: '',
      action:''
    });
  }, [appendCamera]);

  const handleRemoveCamera = useCallback((index: number) => {
    removeCamera(index); 
  }, [removeCamera]);

  const handleLoaiTuoiChange = useCallback((index: number, value: string) => {
    const selectedTuoi = loaiTuoiOptions.find((option: any) => String(option.Value) === String(value));
    setValue(`chiTietLoSam.${index}.loSamLoaiTuoiId`, Number(value));
    setValue(`chiTietLoSam.${index}.tuoi`, (selectedTuoi as any)?.Text || '');
  }, [setValue, loaiTuoiOptions]);

  const onSubmit = useCallback(
    async (data: FormValuesProps) => {
      try {
        let imageUrl = '';

        // Upload image if there's a file
        if (data.coverUrl && typeof data.coverUrl !== 'string' && data.coverUrl instanceof File) {
          console.log('Uploading file:', data.coverUrl);
          imageUrl = await uploadImage(data.coverUrl);
        } else if (typeof data.coverUrl === 'string') {
          // Keep existing image URL
          imageUrl = data.coverUrl;
        } else {
          console.log('No file to upload or file is not a File instance:', data.coverUrl);
        }

        const losamData: LoSamApi = {
          loSam_ID: isEdit && currentLoSam?.loSam_ID ? currentLoSam.loSam_ID : 0,
          vuonTrong_ID: Number(data.vuonTrong_ID) || 0,
          tenLo: data.tenLo,
          maLo: data.maLo,
          ngay: new Date(),
          dienTich: data.dienTich,
          loai: data.loai,
          ghiChu: data.ghiChu,
          soHang: data.soHang,
          soCot: data.soCot,
          hinhAnh: imageUrl,
          trangThai: data.trangThai || 1,
          loSamChiTiets: data.chiTietLoSam,
          loSamCameras: data.cameras,
          sensorIds : data.sensorIds,
        };

        if (isEdit && currentLoSam?.loSam_ID) {
          const response = await editLoSam({
            ...losamData,
          },
            data.coverUrl instanceof File ? data.coverUrl : undefined);
          if (response.messCode === 1 || response.messCode === 6) {
            enqueueSnackbar('Cập nhật lô sâm thành công!', { variant: 'success' });
            window.location.href = paths.dashboard.losam.list;
          } else {
            enqueueSnackbar(response.message || 'Có lỗi xảy ra khi cập nhật lô sâm!', { variant: 'error' });
          }
        } else if (onSave) {
          await onSave(losamData, data.coverUrl instanceof File ? data.coverUrl : undefined);
        } else {
          const response = await addLoSam(losamData, data.coverUrl instanceof File ? data.coverUrl : undefined);
          if (response.messCode === 1 || response.messCode === 6) {
            enqueueSnackbar('Tạo lô sâm thành công!', { variant: 'success' });
            window.location.href = paths.dashboard.losam.list;
          } else {
            enqueueSnackbar(response.message || 'Có lỗi xảy ra khi tạo lô sâm!', { variant: 'error' });
          }
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar(
          isEdit ? 'Có lỗi xảy ra khi cập nhật lô sâm!' : 'Có lỗi xảy ra khi tạo lô sâm!',
          { variant: 'error' }
        );
      }
    },
    [isEdit, currentLoSam?.loSam_ID, addLoSam, editLoSam, uploadImage, enqueueSnackbar, onSave]
  );

  const renderDetails = (
    <>
      {mdUp && (
        <Grid md={3}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Chi tiết
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Thông tin cơ bản của lô sâm...
          </Typography>
        </Grid>
      )}

      <Grid xs={12} md={9}>
        <Card>
          {!mdUp && <CardHeader title="Chi tiết" />}

          <Stack spacing={3} sx={{ p: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Vườn trồng</InputLabel>
              <Select
                name="vuonTrong_ID"
                value={values.vuonTrong_ID}
                label="Vườn trồng"
                onChange={(event) => setValue('vuonTrong_ID', Number(event.target.value))}
                disabled={isEdit}
              >
                {gardens.map((g) => (
                  <MenuItem key={g.vuonTrong_ID} value={g.vuonTrong_ID}>
                    {g.tenVuon}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <RHFTextField name="tenLo" label="Tên lô" />

            <RHFTextField name="maLo" label="Mã lô" />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <RHFTextField
                name="soHang"
                label="Số hàng"
                type="number"
              />

              <RHFTextField
                name="soCot"
                label="Số cột"
                type="number"
              />
            </Stack>

            <RHFTextField
              name="dienTich"
              label="Diện tích (m²)"
              type="number"
            />
            <RHFTextField name="loai" label="Loại" />

            <RHFTextField
              name="ghiChu"
              label="Ghi chú"
              multiline
              rows={3}
            />
              <RHFAutocomplete
                name="trangThai"
                label="Trạng thái"
                options={STATUS_OPTIONS}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.label}
                isOptionEqualToValue={(opt, val) => (typeof val === 'number' ? opt.value === val : opt.value === val.value)}
                value={STATUS_OPTIONS.find((o) => o.value === watch('trangThai')) || null}
                onChange={(e, newValue) => setValue('trangThai', newValue ? (newValue as any).value : 1, { shouldValidate: true })}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>{option.label}</li>
                )}
              />

            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Ảnh bìa</Typography>
              <RHFUpload
                name="coverUrl"
                maxSize={3145728}
                onDrop={handleDrop}
                onDelete={handleRemoveFile}
              />
            </Stack>
          </Stack>
        </Card>
      </Grid>
    </>
  );

  const renderChiTietLoSam = (
    <>
      {mdUp && (
        <Grid md={3}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Chi tiết lô sâm
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Thông tin chi tiết về các loại tuổi sâm trong lô...
          </Typography>
        </Grid>
      )}

      <Grid xs={12} md={9}>
        <Card>
          {!mdUp && <CardHeader title="Chi tiết lô sâm" />}

          <Stack spacing={3} sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Danh sách chi tiết</Typography>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="mingcute:add-line" />}
                onClick={handleAddChiTiet}
              >
                Thêm chi tiết
              </Button>
            </Stack>

            {fields.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Loại tuổi</TableCell>
                      <TableCell>Số lượng</TableCell>
                      <TableCell>Trạng thái</TableCell>
                      <TableCell width={50} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((field, index) => (
                      <TableRow key={field.id}>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={values.chiTietLoSam[index]?.loSamLoaiTuoiId || 1}
                              onChange={(event) => handleLoaiTuoiChange(index, String(event.target.value))}
                            >
                              {loaiTuoiOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.text}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={values.chiTietLoSam[index]?.soLuong || 1}
                            onChange={(event) => setValue(`chiTietLoSam.${index}.soLuong`, Number(event.target.value))}
                            inputProps={{ min: 1 }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={values.chiTietLoSam[index]?.trangThai || 1}
                              onChange={(event) => setValue(`chiTietLoSam.${index}.trangThai`, Number(event.target.value))}
                            >
                              <MenuItem value={1}>Hoạt động</MenuItem>
                              <MenuItem value={2}>Không hoạt động</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveChiTiet(index)}
                          >
                            <Iconify icon="mingcute:delete-2-line" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {fields.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 3,
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2">
                  Chưa có chi tiết lô sâm nào. Nhấn &quot;Thêm chi tiết&quot; để bắt đầu.
                </Typography>
              </Box>
            )}
          </Stack>
        </Card>
      </Grid>
    </>
  );

  const renderCameras = (
    <>
      {mdUp && (
        <Grid md={3}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Camera
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Thông tin camera giám sát lô sâm...
          </Typography>
        </Grid>
      )}

      <Grid xs={12} md={9}>
        <Card>
          {!mdUp && <CardHeader title="Camera" />}

          <Stack spacing={3} sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Danh sách camera</Typography>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="mingcute:add-line" />}
                onClick={handleAddCamera}
              >
                Thêm camera
              </Button>
            </Stack>

            {cameraFields.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Loại camera</TableCell>
                      <TableCell>OnvifCamera</TableCell>
                      <TableCell>IPCamera</TableCell>
                      <TableCell>RTSP</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Password</TableCell>
                      <TableCell width={50} /> 
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cameraFields.map((field, index) => (
                      <TableRow key={field.id}>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={values.cameras[index]?.loSamLoaiCameraId || 1}
                              onChange={(event) => setValue(`cameras.${index}.loSamLoaiCameraId`, Number(event.target.value))}
                            >
                              {loaiCameraOptions.map((option) => (
                                <MenuItem key={option.value || 'key'} value={option.value || ''}>
                                  {option.text}
                                </MenuItem> 
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={values.cameras[index]?.rtsp || ''}
                            onChange={(event) => setValue(`cameras.${index}.rtsp`, event.target.value)}
                            placeholder="rtsp://..."
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={values.cameras[index]?.ipCamera || ''}
                            onChange={(event) => setValue(`cameras.${index}.ipCamera`, event.target.value)}
                            placeholder="192.168.1.1..."
                          />
                        </TableCell>
                         <TableCell>
                          <TextField
                            size="small"
                            value={values.cameras[index]?.onvifCamera || ''}
                            onChange={(event) => setValue(`cameras.${index}.onvifCamera`, event.target.value)}
                            placeholder="0"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={values.cameras[index]?.userName || ''}
                            onChange={(event) => setValue(`cameras.${index}.userName`, event.target.value)}
                            placeholder="admmin..."
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={values.cameras[index]?.password || ''}
                            onChange={(event) => setValue(`cameras.${index}.password`, event.target.value)}
                            type='password'
                            placeholder="1231..."
                          />
                        </TableCell>  
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveCamera(index)}
                          >
                            <Iconify icon="mingcute:delete-2-line" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {cameraFields.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 3,
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2">
                  Chưa có camera nào. Nhấn &quot;Thêm camera&quot; để bắt đầu.
                </Typography>
              </Box>
            )}
          </Stack>
        </Card>
      </Grid>
    </>
  );

  // const renderProperties = (
  //   <>
  //     {mdUp && (
  //       <Grid md={3}>
  //         <Typography variant="h6" sx={{ mb: 0.5 }}>
  //           Thuộc Tính
  //         </Typography>
  //         <Typography variant="body2" sx={{ color: 'text.secondary' }}>
  //           Các chức năng và thuộc tính...
  //         </Typography>
  //       </Grid>
  //     )}
 
  //     <Grid xs={12} md={9}>
  //       <Card>
  //         {!mdUp && <CardHeader title="Thuộc tính" />}

  //         <Stack spacing={3} sx={{ p: 3 }}>
  //           <RHFSwitch name="is_public" label="Công bố" />
  //         </Stack>
  //       </Card>
  //     </Grid>
  //   </>
  // );
const rendersensordivice = (
  <>
    {mdUp && (
      <Grid md={3}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Thiết bị cảm biến
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Các thiết bị cảm biến nhiệt độ, độ ẩm
        </Typography>
      </Grid>
    )}

    <Grid xs={12} md={9}>
      <Card>
        {!mdUp && <CardHeader title="Thiết bị cảm biến" />}

        <Stack spacing={3} sx={{ p: 3 }}>
          <RHFAutocomplete
            name="sensorIds"
            label="Chọn thiết bị cảm biến"
            placeholder="+ Thiết bị"
            multiple
            freeSolo={false}
            options={currentLoSam?.sensorModels || []}
            getOptionLabel={(option) => {
              if (!option) return '';
              return typeof option === 'object' 
                ? `${option.sensorCode} - ${option.jValue} (${option.unit})`
                : String(option);
            }}
            isOptionEqualToValue={(option, value) => 
              typeof option === 'object' && typeof value === 'object'
                ? option.sensorId === value.sensorId
                : option === value
            }
            // Map sensorIds sang objects khi edit
            value={
              values.sensorIds?.map(id => 
                currentLoSam?.sensorModels?.find(s => s.sensorId === id)
              ).filter(Boolean) || []
            }
            renderOption={(props, option) => (
              <li {...props} key={option?.sensorId}>
                <Iconify icon="mdi:sensor" width={24} sx={{ mr: 1 }} />
                <Stack>
                  <Typography variant="body2">
                    {option?.sensorCode} - {option?.jValue}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Đơn vị: {option?.unit}
                  </Typography>
                </Stack>
              </li>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={typeof option === 'object' ? option.sensorId : index}
                  label={
                    typeof option === 'object' 
                      ? `${option.sensorCode} - ${option.jValue}`
                      : option
                  }
                  size="small"
                  color="primary"
                  variant="soft"
                />
              ))
            }
            onChange={(event, newValue) => {
              // Chuyển đổi từ object sang array of IDs
              const ids = newValue
                .map(item => (typeof item === 'object' ? item.sensorId : item))
                .filter((id): id is number => typeof id === 'number');
              setValue('sensorIds', ids);
            }}
          />

          {/* Hiển thị danh sách các thiết bị đã chọn */}
          {values.sensorIds && values.sensorIds.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Thiết bị đã chọn ({values.sensorIds.length})
              </Typography>
              <Stack spacing={1}>
                {values.sensorIds.map((sensorId) => {
                  const sensor = currentLoSam?.sensorModels?.find(s => s.sensorId === sensorId);
                  if (!sensor) return null;
                  
                  return (
                    <Box
                      key={sensorId}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Iconify icon="mdi:sensor" width={24} />
                        <Stack>
                          <Typography variant="body2">
                            {sensor.sensorCode} - {sensor.jValue}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Đơn vị: {sensor.unit}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Stack>
      </Card>
    </Grid>
  </>
);
  const renderActions = (
    <>
      {mdUp && <Grid md={3} />}
      <Grid xs={12} md={9} sx={{ display: 'flex', alignItems: 'center' }}>
        <Button color="inherit" variant="outlined" size="large" onClick={onClose}>
          Hủy
        </Button>

        <CustomLoadingButton
          type="submit"
          variant="contained"
          size="large"
          loading={isSubmitting}
          sx={{ ml: 2 }}
        >
          {isEdit ? 'Cập nhật' : 'Tạo mới'}
        </CustomLoadingButton>
      </Grid>
    </>
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        {renderDetails}

        {renderChiTietLoSam}

        {renderCameras}

        {rendersensordivice}

        {/* {renderProperties} */}

        {renderActions}

      </Grid>
    </FormProvider>
  ); 
}
